<template>
    <div>
        <div class="pb-10 mb-12 border-b border-gray-200">
            <h2 class="pb-1 text-2xl font-bold text-left text-black no-underline normal-case non-italic">Suitability</h2>
            <p class="text-lg font-normal text-left no-underline normal-case non-italic">We want to match you with the investment opportunities that fit who you are.</p>
        </div>

        <app-errors :form="form" class="mb-10"></app-errors>

        <p class="pb-5 text-lg font-medium">We are required to check that you are eligible for investments you make with us and that these investments are reasonably suitable for your situation. Please complete the following to help us make these determinations regarding your eligibility and suitability.</p>
        <p class="pb-4 text-lg font-medium text-gray-500">Please provide your best estimate to the following for your household:</p>

        <div class="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2">
            <div class="col-span-1">
                <select-field v-model="form.annualIncome" label="Annual Income" label-by="label" track-by="value" :required="true" :options="annualIncomeOptions"></select-field>
            </div>

            <div class="col-span-1">
                <select-field v-model="form.netWorth" label="Net Worth" label-by="label" track-by="value" :required="true" :options="netWorthOptions"></select-field>
            </div>

            <div v-show="form.annualIncome == '200k_300k'" class="col-span-1">
                <text-field v-model="form.primaryIncome" v-currency="{'prefix': '$'}" label="Primary Income" :required="true"></text-field>
            </div>

            <div v-show="form.annualIncome == '200k_300k'" class="col-span-1">
                <text-field v-model="form.spouseIncome" v-currency="{'prefix': '$'}" label="Spouse Income" :required="true"></text-field>
            </div>

            <div v-show="form.annualIncome == '200k_300k' && showIncomeErrorMessage" class="col-span-1 sm:col-span-2">
                <div class="px-6 py-4 text-red-900 bg-red-200 rounded-md">Please check your <strong>Primary Income</strong> and <strong>Spouse Income</strong>.</div>
            </div>

            <div class="col-span-1">
                <select-field v-model="form.liquidNetWorth" label="Liquid Net Worth" label-by="label" track-by="value" :required="true" :options="liquidNetWorthOptions"></select-field>
            </div>

            <div class="col-span-1">
                <select-field v-model="form.taxRate" label="Tax Rate" label-by="label" track-by="value" :required="true" :options="taxRateOptions"></select-field>
            </div>
        </div>

        <div v-show="showNsightIrrevocableMessage" class="my-4 rounded-md bg-[#FFF3CD] p-4 text-sm text-yellow-700 space-y-4">
            <p>This investment is only eligible for irrevocable trusts with assets of greater than $5 million. If the trust is revocable please go back and choose that option rather than irrevocable, otherwise, a different ownership method is needed for this investment.</p>
        </div>

        <div v-show="showAccreditedMessage" class="mb-8 rounded-md bg-[#FFF3CD] p-4 text-sm text-yellow-700 space-y-4">
            <p class="text-lg font-medium text-yellow-800">All investors must be <a href="https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated-3" class="font-bold" target="_blank">accredited</a> in order to proceed.</p>
            <p>Please check your <strong>Net Worth</strong> and <strong>Income</strong> to verify they are correct. You will be asked to verify your information later in this process.</p>

            <p>For additional information please contact <a href="mailto:investorrelations@mammothresearch.com" class="font-bold">investorrelations@mammothresearch.com</a>.</p>
        </div>

        <div v-show="!showAccreditedMessage" class="mb-12">
            <h3 class="pb-5 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">This investment represents the following portion of my financial portfolio:</h3>
            <div class="space-y-4">
                <template v-for="(option, index) in financialPortfolioOptions" :key="index">
                    <radio-field v-model="form.financialPortfolio" :value="option.value">{{ option.label }}</radio-field>
                </template>
            </div>
        </div>

        <div v-show="!showAccreditedMessage" class="mb-12">
            <h3 class="pb-2 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Source of Funds</h3>
            <p class="pb-4 text-lg font-medium text-gray-500">What is the source of the funds you are using for this investment?</p>
            <div class="space-y-4">
                <template v-for="(option, index) in sourceFundsOptions" :key="index">
                    <radio-field v-model="form.sourceFunds" :value="option.value" :disabled="profileForm.investingType == 'ira' && option.value != 'IRA'">{{ option.label }}</radio-field>
                </template>
            </div>
        </div>

        <div v-show="!showAccreditedMessage" class="mb-12">
            <p class="pb-4 text-lg font-medium text-gray-500">Please describe your prior experience with private investments:</p>
            <div class="space-y-4">
                <template v-for="(option, index) in priorExperienceOptions" :key="index">
                    <radio-field v-model="form.priorExperience" :value="option.value">{{ option.label }}</radio-field>
                </template>
            </div>
        </div>

        <div v-show="!showAccreditedMessage" class="mb-6">
            <h3 class="pb-1 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Preferred Advisor Partner Code</h3>
            <p class="pb-4 text-sm text-gray-500">This field is optional. If you have a Preferred Advisor Partner Code, please enter it here.</p>
            <text-field v-model="form.partnerCode" maxlength="25"></text-field>
        </div>

        <div v-show="!showAccreditedMessage" class="mb-7">
            <h3 class="pb-5 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Do you have a financial advisor or CPA you want us to share information with?</h3>
            <div class="flex items-center space-x-8">
                <radio-field v-model="form.financialAdvisor" value="Yes">Yes</radio-field>
                <radio-field v-model="form.financialAdvisor" value="No">No</radio-field>
            </div>
        </div>

        <div v-show="!showAccreditedMessage && form.financialAdvisor == 'Yes'" class="mb-12">
            <h3 class="pb-5 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Financial Advisor/CPA Information</h3>
            <p class="pb-4 text-lg font-medium text-gray-500">If you choose to provide your financial advisor's information, we will share your investment information directly with them on your behalf.</p>

            <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div class="col-span-1">
                    <text-field label="First Name" v-model="form.advisorFirstName" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field label="Last Name" v-model="form.advisorLastName" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field label="Firm Name" v-model="form.advisorFirm" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field label="Email Address" v-model="form.advisorEmail" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field label="Phone" v-model="form.advisorPhone" v-maska="'(###) ###-####'" :required="true"></text-field>
                </div>
            </div>
        </div>

        <div v-show="!showAccreditedMessage" class="mb-12">
            <h3 class="pb-2 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Trusted Contact (optional)</h3>
            <p class="mb-4 text-sm font-medium">I have a trusted contact that I would like to have contacted in event the issuer, placement agent, registered representative, or affiliates have questions or concerns about my whereabouts or health status; suspect that I may be a victim of fraud or financial exploitation; suspect that I might no longer be able to handle my financial affairs; to confirm identity of any legal guardian, executor, trustee, authorized party, or power attorney; has any other concerns; or is unable to contact me about my investment.</p>

            <div class="flex items-center space-x-8">
                <radio-field v-model="form.trustedContact" value="Yes">Yes</radio-field>
                <radio-field v-model="form.trustedContact" value="No">No</radio-field>
            </div>

            <div v-show="form.trustedContact == 'Yes'" class="pt-12">
                <p class="pb-4 text-lg font-medium text-gray-500"><span class="text-gray-700">Note:</span> Your trusted contact must be someone other than the investment owner.</p>

                <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div class="col-span-1">
                        <text-field label="Contact's First Name" v-model="form.contactFirstName" :required="true"></text-field>
                    </div>

                    <div class="col-span-1">
                        <text-field label="Contact's Last Name" v-model="form.contactLastName" :required="true"></text-field>
                    </div>

                    <div class="col-span-1">
                        <text-field label="Contact's Phone" v-model="form.contactPhone" v-maska="'(###) ###-####'" :required="true"></text-field>
                    </div>

                    <div class="col-span-1">
                        <text-field label="Contact's Email Address" v-model="form.contactEmail" :required="true"></text-field>
                    </div>

                    <div class="col-span-2">
                        <text-field label="Contact's Relationship to You" v-model="form.contactRelation" :required="true"></text-field>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-6 sm:flex sm:justify-end">
            <div class="inline-flex items-center space-x-3">
                <app-button @click="handleBack" class="!border-gray-200 !bg-white !text-gray-400">Back</app-button>

                <app-button @click="handleNext" :busy="form.busy" :disabled="showAccreditedMessage || showIncomeErrorMessage || showNsightIrrevocableMessage">Save and Continue</app-button>
            </div>
        </div>
    </div>
</template>

<script>
import AppButton from '@Components/Button'
import AppErrors from '@Components/Errors'
import TextField from '@Fields/Text'
import SelectField from '@Fields/Select'
import RadioField from '@Fields/Radio'
import CheckboxField from '@Fields/Checkbox'
import RangeField from '@Fields/Range'
import useForm from '@/form'
import { useStore } from 'vuex'
import { computed, onMounted } from 'vue'

export default {
    components: {
        AppButton,
        AppErrors,
        TextField,
        SelectField,
        RadioField,
        CheckboxField,
        RangeField,
    },
    props: ['series'],
    emits: ['prev', 'next', 'process', 'complete', 'goto'],
    setup(props, { emit }){
        const store = useStore()

        const form = useForm(_.merge({
            annualIncome: '',
            netWorth: '',
            primaryIncome: '',
            spouseIncome: '',
            liquidNetWorth: '',
            taxRate: '',
            financialPortfolio: 'less_10',
            sourceFunds: '',
            priorExperience: '',
            partnerCode: '',
            financialAdvisor: 'No',
            advisorFirstName: '',
            advisorLastName: '',
            advisorFirm: '',
            advisorEmail: '',
            advisorPhone: '',
            trustedContact: 'No',
            contactFirstName: '',
            contactLastName: '',
            contactPhone: '',
            contactEmail: '',
            contactRelation: ''
        }, store.state.suitabilityForm));

        const profileForm = store.state.profileForm

        onMounted(() => {
            window.scroll(0, 0)

            if (profileForm.investingType == 'ira') {
                form.sourceFunds = 'IRA'
            }
        })

        const showAccreditedMessage = computed(() => {
            return _.includes(['less_than_100k', '100k_1m'], form.netWorth) && _.includes(['less_than_100k', '100k_200k'], form.annualIncome)
        })

        const showIncomeErrorMessage = computed(() => {
            const primaryIncome = form.primaryIncome.replace(/[^0-9.]/g, '')
            const spouseIncome = form.spouseIncome.replace(/[^0-9.]/g, '')

            if (form.annualIncome != '200k_300k') {
                return false
            }

            if (primaryIncome < 200000 && spouseIncome < 200000) {
                if ((parseFloat(primaryIncome)+parseFloat(spouseIncome)) >= 300000) {
                    return false
                }

                return true
            }

            return false
        })

        const isNsightEntityIrrevocable = computed(() => {
            return props.series == 'nsight' && store.state.profileForm.investingType == 'entity' && store.state.profileForm.entity.entityType == 'Irrevocable Trust'
        })

        const showNsightIrrevocableMessage = computed(() => {
            return isNsightEntityIrrevocable.value && form.netWorth != '5m_plus'
        })

        function handleBack(){
            emit('prev', true)
        }

        function handleNext(){
            form.post(route('intake.validate.suitability')).then(response => {
                store.commit('UPDATE_FORM_DATA', {
                    form: 'suitabilityForm',
                    data: form.data()
                });

                emit('next', true)
            }).catch(errors => {
                window.scroll(0,0)
                console.log(errors.response)
            })
        }

        return {
            form,
            profileForm,
            showAccreditedMessage,
            showIncomeErrorMessage,
            showNsightIrrevocableMessage,
            handleBack,
            handleNext
        }
    },
    computed: {
        yearData(){
            return _.range(1, 40, 1);
        },
        annualIncomeOptions(){
            return [{
                label: 'Less than $100,000',
                value: 'less_than_100k'
            },
            {
                label: '$100,000 - $200,000',
                value: '100k_200k'
            },
            {
                label: '$200,000 - $300,000',
                value: '200k_300k'
            },
            {
                label: '$300,000 - $750,000',
                value: '300k_750k'
            },
            {
                label: '$750,000 +',
                value: '750k_plus'
            }]
        },
        netWorthOptions(){
            return [{
                label: 'Less than $100,000',
                value: 'less_than_100k'
            },
            {
                label: '$100,000 - $1,000,000',
                value: '100k_1m'
            },
            {
                label: '$1,000,000 - $2,200,000',
                value: '1m_2p2m'
            },
            {
                label: '$2,200,000 - $5,000,000',
                value: '2p2m_5m'
            },
            {
                label: '$5,000,000 +',
                value: '5m_plus'
            }]
        },
        liquidNetWorthOptions(){
            return [{
                label: 'Less than $100,000',
                value: 'less_than_100k'
            },
            {
                label: '$100,000 - $500,000',
                value: '100k_500k'
            },
            {
                label: '$500,000 - $1,000,000',
                value: '500k_1m'
            },
            {
                label: '$1,000,000 - $4,000,000',
                value: '1m_4m'
            },
            {
                label: '$4,000,000 +',
                value: '4m_plus'
            }]
        },
        taxRateOptions(){
            return [{
                label: '15% or less',
                value: '15_or_less'
            },
            {
                label: '16% - 25%',
                value: '16_25'
            },
            {
                label: '26% - 35%',
                value: '26_35'
            },
            {
                label: '36%+',
                value: '35_plus'
            }]
        },
        financialPortfolioOptions(){
            return [{
                label: 'Less than 10%',
                value: 'less_10'
            },
            {
                label: '11% - 15%',
                value: '11_15'
            },
            {
                label: '15% - 20%',
                value: '15_20'
            },
            {
                label: '20% - 67%',
                value: '20_67'
            },
            {
                label: '68% - 100%',
                value: '68_100'
            }]
        },
        sourceFundsOptions(){
            return [{
                label: 'My Income',
                value: 'Income'
            },{
                label: 'My Savings',
                value: 'Savings'
            },{
                label: 'Cash from My IRA Account',
                value: 'IRA'
            },{
                label: 'Gift',
                value: 'Gift'
            },{
                label: 'A Foreign Account',
                value: 'ForeignAccount'
            }]
        },
        priorExperienceOptions(){
            return [{
                label: 'This is my first time investing in a private investment',
                value: 'FirstPrivateInvestment'
            }, {
                label: 'I\'ve previously invested in 1-3 private investments',
                value: '1 to 3'
            }, {
                label: 'I\'ve previously invested in 4-10 private investments',
                value: '4 to 10'
            }, {
                label: 'I\'ve previously invested in more than 10 other private investments',
                value: 'More than 10'
            },]
        }
    }
}
</script>

<style>

</style>
